import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {pageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {fetchWithTracing} from '../../helpers/fetch-with-tracing'
import {getStackCondition} from '../../helpers/json-api-helpers'
import {useQueries, useQueryClient} from '@github-ui/react-query'
import type {StackMergeabilityPageData} from '../payloads/stack-mergeability'
import type {PullRequestMergeRequirementsPayload} from '../payloads/merge-box'
import type {MergeMethod} from '../../types'

export function stackMergeabilityPageDataQueryKey(basePageDataUrl: string, currentPullRequestId: string) {
  return [
    PageData.stackMergeability,
    `basePageDataURL:${basePageDataUrl}`,
    `currentPullRequestId:${currentPullRequestId}`,
  ]
}

export function useStackMergeabilityQuery({
  basePageDataUrl,
  currentPullRequestId,
  enabled = true,
  mergeMethod,
  bypassRequirements = false,
}: {
  basePageDataUrl: string
  currentPullRequestId: string
  mergeMethod: MergeMethod
  enabled?: boolean
  bypassRequirements?: boolean
}) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStackMergeabilityQueries({
    pulls: [{enabled, basePageDataUrl}],
    currentPullRequestId,
    mergeMethod,
    bypassRequirements,
  })[0]!
}

export function useStackMergeabilityQueries({
  pulls,
  currentPullRequestId,
  mergeMethod,
  bypassRequirements = false,
}: {
  pulls: Array<{enabled: boolean; basePageDataUrl: string}>
  currentPullRequestId: string
  mergeMethod: MergeMethod
  bypassRequirements?: boolean
}) {
  const queryClient = useQueryClient()

  const searchParams = new URLSearchParams()
  searchParams.append('merge_method', mergeMethod)
  searchParams.append('bypass_requirements', bypassRequirements.toString())

  const queries = pulls.map(({enabled, basePageDataUrl}) => {
    const apiURL = pageDataUrl(basePageDataUrl, PageData.stackMergeability, searchParams.toString())
    const queryKey = stackMergeabilityPageDataQueryKey(basePageDataUrl, currentPullRequestId)
    return {
      queryKey,
      queryFn: async () => {
        return fetchWithTracing<StackMergeabilityPageData>(apiURL, queryKey, queryClient)
      },
      staleTime: Infinity,
      enabled,
    }
  })

  return useQueries({queries})
}

/**
 * Runs a `stack_mergeability` query for every entry in the current pull request's stack
 * condition, keyed off `mergeRequirements` so callers don't have to derive the `pulls` list themselves.
 */
export function useStackEntryMergeabilityQueries({
  mergeRequirements,
  currentPullRequestId,
  mergeMethod,
  bypassRequirements = false,
}: {
  mergeRequirements: PullRequestMergeRequirementsPayload | null
  currentPullRequestId: string
  mergeMethod: MergeMethod
  bypassRequirements?: boolean
}) {
  return useStackMergeabilityQueries({
    pulls: (getStackCondition(mergeRequirements)?.entries ?? []).map(entry => ({
      basePageDataUrl: entry.pull.url,
      enabled: entry.status === 'NEEDS_MERGE_STATE',
    })),
    currentPullRequestId,
    mergeMethod,
    bypassRequirements,
  })
}
